import React from "react";
import { Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/AppContext";
import api from "@/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export default function CartDrawer({ open, onOpenChange }) {
  const { t, cart, setCart, refreshCart } = useApp();

  const total = cart.reduce((s, i) => s + Number(i.price || 0) * Number(i.quantity || 1), 0);

  const remove = async (id) => {
    const { data } = await api.post(`/cart/remove/${id}`);
    setCart(data.items || []);
  };

  const checkout = async () => {
    try {
      await api.post("/checkout");
      await refreshCart();
      toast.success(t("payment_placeholder"));
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-white border-l border-cyan-500/30 w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-head text-xl text-slate-900">{t("cart")}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-3">
          {cart.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-10" data-testid="cart-empty">{t("cart_empty")}</p>
          ) : (
            cart.map((item, i) => (
              <div key={item.item_id} className="ps-glow-card rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`cart-item-${i}`}>
                <div className="min-w-0">
                  <p className="font-mono-ps text-cyan-700 text-xs">{item.pn}</p>
                  <p className="text-slate-900 text-sm truncate">{item.name}</p>
                  <p className="text-slate-500 text-xs">{item.seller && item.seller !== "GetFPV" ? item.seller : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-display font-bold text-slate-900">${Number(item.price || 0).toLocaleString()}</span>
                  <button data-testid={`cart-remove-${i}`} onClick={() => remove(item.item_id)} className="text-red-400 hover:text-red-300">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-slate-200 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600 font-head">{t("total")}</span>
              <span className="font-display text-2xl font-black text-cyan-600">${total.toLocaleString()}</span>
            </div>
            <Button
              data-testid="checkout-btn"
              onClick={checkout}
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-head font-bold py-6"
            >
              <CreditCard size={18} className="mr-2" /> {t("checkout")}
            </Button>
            <p className="text-[11px] text-slate-500 text-center">{t("payment_placeholder")}</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
